import {
  ArrowUUpRight,
  DotsThreeVertical,
  SignOut,
  Trash,
  UserGear,
} from "@phosphor-icons/react";

import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import useCustomer from "common/datahooks/useCustomer";
import type { Member } from "common/types";

import useMembersMutations from "../../datahooks/useMembersMutations";

import EnvelopeX from "assets/icons/envelope-x.svg?react";

interface MemberActionsDropdownProps {
  member: Member;
  setAction: (newAction: "remove" | "revoke" | "permissions") => void;
}

export default function MemberActionsDropdown({
  member,
  setAction,
}: MemberActionsDropdownProps) {
  const { resendInvite, isResendingInvite } = useMembersMutations();

  const { customerInfo } = useCustomer();
  const { email: customerEmail } = customerInfo;
  const { email, token: inviteToken, status } = member;
  const isCustomerWorkspace = customerEmail === email;

  let dropdownContent =
    status === "pending" ? (
      <>
        <DropdownMenuItem
          onClick={() => {
            resendInvite({ inviteToken });
          }}
        >
          <ArrowUUpRight />
          Resend invite
        </DropdownMenuItem>

        <DropdownMenuItem variant="danger" onClick={() => setAction("revoke")}>
          <EnvelopeX />
          Revoke invite
        </DropdownMenuItem>
      </>
    ) : (
      <>
        <RenderIf condition={status === "accepted" && customerEmail !== email}>
          <DropdownMenuItem onClick={() => setAction("permissions")}>
            <UserGear />
            Update permissions
          </DropdownMenuItem>
        </RenderIf>

        <DropdownMenuItem variant="danger" onClick={() => setAction("remove")}>
          <Trash />
          Remove from workspace
        </DropdownMenuItem>
      </>
    );

  if (isCustomerWorkspace) {
    dropdownContent = (
      <DropdownMenuItem variant="danger" onClick={() => setAction("remove")}>
        <SignOut />
        Leave workspace
      </DropdownMenuItem>
    );
  }

  const dropdownMenuComponent = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          intent="iconOnly"
          variant="secondary-black"
          size="sm"
          isLoading={isResendingInvite}
        >
          <DotsThreeVertical />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="z-aboveDialog" align="end">
        <DropdownMenuList>{dropdownContent}</DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      {isCustomerWorkspace ? (
        dropdownMenuComponent
      ) : (
        <RestrictedComponent visibleForPermissions={["members_full"]}>
          {dropdownMenuComponent}
        </RestrictedComponent>
      )}
    </>
  );
}
